/**
 * ThemeContext.js - Theme/Dark Mode Context
 * 
 * Manages dark mode preference and provides CPLC brand colors.
 * Also supports custom header colors per user.
 * Color palette:
 *   - Green: #9CD3A5
 *   - Blue: #77B8DB
 *   - Red: #FF8282
 *   - Pink: #FFBCBC
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import dataStore from '../data/dataStore';

const ThemeContext = createContext(null);

// CPLC Brand Colors
export const COLORS = {
  green: '#9CD3A5',
  blue: '#77B8DB',
  red: '#FF8282',
  pink: '#FFBCBC',
  purple: '#9575CD',
  orange: '#FFB74D',
  // Darker variants for text/contrast
  greenDark: '#7AB885',
  blueDark: '#5A9BC0',
  redDark: '#E56B6B',
  pinkDark: '#E5A3A3'
};

// Preset header color options
export const HEADER_COLOR_OPTIONS = [
  { name: 'Green (Default)', value: '#9CD3A5' },
  { name: 'Blue', value: '#77B8DB' },
  { name: 'Navy', value: '#0f3460' },
  { name: 'Purple', value: '#7E57C2' },
  { name: 'Teal', value: '#26A69A' },
  { name: 'Orange', value: '#FF8A65' },
  { name: 'Pink', value: '#F06292' },
  { name: 'Slate', value: '#546E7A' },
  { name: 'Indigo', value: '#5C6BC0' },
  { name: 'Red', value: '#EF5350' }
];


// Theme configurations
export const themes = {
  light: {
    name: 'light',
    background: '#ffffff',
    backgroundSecondary: '#f8f9fa',
    text: '#333333',
    textSecondary: '#666666',
    border: '#e0e0e0',
    cardBackground: '#ffffff',
    headerBackground: COLORS.green,
    headerText: '#ffffff',
    primary: COLORS.blue,
    success: COLORS.green,
    danger: COLORS.red,
    warning: COLORS.pink
  },
  dark: {
    name: 'dark',
    background: '#1a1a2e',
    backgroundSecondary: '#16213e',
    text: '#e0e0e0',
    textSecondary: '#a0a0a0',
    border: '#3a3a5a',
    cardBackground: '#242444',
    headerBackground: '#0f3460',
    headerText: '#e0e0e0',
    primary: COLORS.blue,
    success: COLORS.green,
    danger: COLORS.red,
    warning: COLORS.pink
  }
};


export function ThemeProvider({ children }) {
  const { user, refreshUser } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const [customHeaderColor, setCustomHeaderColor] = useState(null);

  // Sync dark mode and custom header color with user preference
  useEffect(() => {
    if (user) {
      setDarkMode(user.darkMode || false);
      setCustomHeaderColor(user.headerColor || null);
    }
  }, [user]);

  // Toggle dark mode
  const toggleDarkMode = async () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    
    // Save to user profile if logged in (no audit log for preference changes)
    if (user) {
      await dataStore.updateUserDarkMode(user.id, newValue);
      refreshUser();
    }
  };

  // Set custom header color
  const setHeaderColor = async (color) => {
    setCustomHeaderColor(color);
    
    // Save to user profile if logged in (no audit log for preference changes)
    if (user) {
      await dataStore.updateUserHeaderColor(user.id, color);
      refreshUser();
    }
  };

  // Get current theme with custom header color override
  const baseTheme = darkMode ? themes.dark : themes.light;
  const theme = {
    ...baseTheme,
    headerBackground: customHeaderColor || baseTheme.headerBackground
  };

  // Apply theme to document body
  useEffect(() => {
    document.body.style.backgroundColor = theme.background;
    document.body.style.color = theme.text;
    
    // Apply background image based on theme
    if (!darkMode) {
      document.body.style.backgroundImage = 'url(/assets/backgrounds/login-bg.jpg)';
    } else {
      document.body.style.backgroundImage = 'url(/assets/backgrounds/login-bg-dark.jpg)';
    }
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundRepeat = 'no-repeat';
    
    // Use fixed attachment on desktop, scroll on mobile (iOS doesn't support fixed well)
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      document.body.style.backgroundAttachment = 'scroll';
      document.body.style.minHeight = '100vh';
    } else {
      document.body.style.backgroundAttachment = 'fixed';
    }
  }, [theme, darkMode]);


  const value = {
    darkMode,
    toggleDarkMode,
    theme,
    colors: COLORS,
    headerColorOptions: HEADER_COLOR_OPTIONS,
    customHeaderColor,
    setHeaderColor
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
